import React, { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList } from "recharts";
import api from "../../lib/api";
import { Card } from "../ui/card";
import { RangePicker, RangeNote, fmtDay } from "./RangePicker";

// Under an hour stays in plain minutes; past that it reads as H:MM h.
export const fmtMins = (m) => {
  const mins = Math.round(m || 0);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")} h`;
};

// "3 Nov. 3:15pm"
export const fmtLastActive = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true })
    .replace(/\s/g, "").toLowerCase();
  return `${day}. ${time}`;
};

export default function MyActivityCard() {
  const [period, setPeriod] = useState("7");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (period === "custom" && !(custom.start && custom.end)) {
      setError("Pick both a start and an end date");
      return;
    }
    const params = period === "custom" ? { period, start: custom.start, end: custom.end } : { period };
    api.get("/activity/me", { params })
      .then((r) => { setData(r.data); setError(""); })
      .catch((err) => setError(err.response?.data?.detail || "Could not load your activity"));
  }, [period, custom]);

  const series = (data?.days || []).map((d) => ({ ...d, label: fmtDay(d.date) }));

  return (
    <Card className="p-3 h-full" data-testid="my-activity">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />My Activity
          </h3>
          <p className="text-[10px] text-muted-foreground">Hours you spent working in the CRM</p>
        </div>
        <RangePicker value={period} onChange={setPeriod} custom={custom} onCustom={setCustom} testid="my-range" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        {[
          ["Average a day", `${data?.avg_hours ?? 0} h`, "my-avg-hours"],
          ["Total in range", `${data?.total_hours ?? 0} h`, "my-total-hours"],
          ["Today so far", `${data?.today_hours ?? 0} h`, "my-today-hours"],
          ["Minutes logged today", `${data?.today_minutes ?? 0} min`, "my-today-minutes"],
        ].map(([label, value, testid]) => (
          <div key={testid} className="rounded-md border border-border bg-secondary/40 p-2">
            <div className="font-heading text-base font-bold text-primary" data-testid={testid}>{value}</div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground leading-tight">{label}</div>
          </div>
        ))}
      </div>

      <div className="h-36 w-full min-w-0 mt-3 overflow-x-auto">
        {error && (
          <div className="text-xs text-destructive mb-2" data-testid="my-activity-error">{error}</div>
        )}
        <ResponsiveContainer width="100%" height="100%" minHeight={120} debounce={50}>
          <BarChart data={series} margin={{ top: 12, right: 8, left: -24, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E2D8" vertical={false} />
            <XAxis dataKey="label" stroke="#666" fontSize={10} interval="preserveStartEnd" />
            <YAxis stroke="#666" fontSize={10} unit="h" />
            <Tooltip formatter={(v) => [`${v} h`, "Active"]}
              contentStyle={{ background: "white", border: "1px solid #E2E2D8", borderRadius: 6 }} />
            {data?.avg_hours > 0 && (
              <ReferenceLine y={data.avg_hours} stroke="#C1440E" strokeDasharray="4 4"
                label={{ value: `avg ${data.avg_hours} h`, position: "insideTopRight", fontSize: 10, fill: "#C1440E" }} />
            )}
            <Bar dataKey="hours" fill="#355E3B" radius={[3, 3, 0, 0]} maxBarSize={38} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <RangeNote range={data?.range} />
    </Card>
  );
}

// Manager view: average daily active hours per sales person, tallest first.
export function TeamActivityCard() {
  const [period, setPeriod] = useState("1");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (period === "custom" && !(custom.start && custom.end)) {
      setError("Pick both a start and an end date");
      return;
    }
    const params = period === "custom" ? { period, start: custom.start, end: custom.end } : { period };
    api.get("/activity/team", { params })
      .then((r) => { setData(r.data); setError(""); })
      .catch((err) => setError(err.response?.data?.detail || "Could not load team activity"));
  }, [period, custom]);

  const rows = data?.rows || [];
  return (
    <Card className="p-6" data-testid="team-activity">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />Team Activity
          </h3>
          <p className="text-xs text-muted-foreground">Time each sales person spends in the CRM — today by default, use the links for earlier days</p>
        </div>
        <RangePicker value={period} onChange={setPeriod} custom={custom} onCustom={setCustom} testid="team-range" />
      </div>

      <div className="mt-5 w-full min-w-0 overflow-x-auto" style={{ height: Math.max(180, rows.length * 48) }}>
        {error && (
          <div className="text-xs text-destructive mb-2" data-testid="team-activity-error">{error}</div>
        )}
        <ResponsiveContainer width="100%" height="100%" minHeight={160} debounce={50}>
          <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 70, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E2D8" horizontal={false} />
            <XAxis type="number" stroke="#666" fontSize={10} allowDecimals={false} tickFormatter={(v) => fmtMins(v)} />
            <YAxis type="category" dataKey="name" stroke="#666" fontSize={11} width={140} />
            <Tooltip formatter={(v) => [`${fmtMins(v)} a day`, "Average"]}
              contentStyle={{ background: "white", border: "1px solid #E2E2D8", borderRadius: 6 }} />
            <Bar dataKey="avg_minutes" fill="#355E3B" radius={[0, 3, 3, 0]} maxBarSize={26}>
              <LabelList dataKey="avg_minutes" position="right" fontSize={11} formatter={(v) => fmtMins(v)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="team-activity-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="py-2 pr-3">Sales Person Name</th>
              <th className="py-2 pr-3">Today's Avg</th>
              <th className="py-2 pr-3">Last 7 Day Avg</th>
              <th className="py-2 pr-3">Last 15 Day Avg</th>
              <th className="py-2 pr-3">Last 30 Day Avg</th>
              <th className="py-2 pr-3">Last 90 Day Avg</th>
              <th className="py-2">Last Active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr><td colSpan={7} className="py-3 text-muted-foreground italic">No sales staff to report on yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.user_id} data-testid={`activity-row-${r.user_id}`}>
                <td className="py-2 pr-3 font-medium">{r.name}</td>
                <td className="py-2 pr-3 font-semibold text-primary" data-testid={`avg-today-${r.user_id}`}>{fmtMins(r.avg_today_minutes)}</td>
                <td className="py-2 pr-3">{fmtMins(r.avg_7_minutes)}</td>
                <td className="py-2 pr-3">{fmtMins(r.avg_15_minutes)}</td>
                <td className="py-2 pr-3">{fmtMins(r.avg_30_minutes)}</td>
                <td className="py-2 pr-3">{fmtMins(r.avg_90_minutes)}</td>
                <td className="py-2 text-muted-foreground" data-testid={`last-active-${r.user_id}`}>
                  {fmtLastActive(r.last_seen)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RangeNote range={data?.range} />
    </Card>
  );
}
